import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { take } from 'rxjs';
import { DownloadService } from './download.service';

import { API_URL } from '@constants';
import type {
  Community,
  CommunityAzureAd,
  CommunityTemplate,
  ConditionsOfUse,
  ConditionsOfUseUpdate,
  ContractTemplate,
  LdapSettings,
  NewCommunityConfig,
  NotificationSettings,
} from '@models';
import { ModalService } from '@services';
import { downloadBlob, getAbsoluteApiUrl } from '@shared/utils';

interface CommunityProcessStatus {
  active: boolean;
  percent: number;
  progress: number;
  total: number;
}

export interface CommunityConfigResponse {
  authenticationMethods?: { id: number; name: string }[];
  recaptchaSiteKey?: string;
  geolocationSupport?: number[];
}

@Service()
export class CommunityService {
  downloadService = inject(DownloadService);
  modalService = inject(ModalService);
  http = inject(HttpClient);

  getCommunityImage = (updateDate: number) =>
    this.http.get(
      `${API_URL}/community/image?base64=true&updateDate=${updateDate}`,
      {
        responseType: 'text',
      }
    );

  getNewCommunityConfig = () =>
    this.http.get<NewCommunityConfig>(`${API_URL}/communities/new/config`);

  getCommunity = () => this.http.get<Community>(`${API_URL}/community`);

  updateCommunity = (body: Partial<Community & { logo: string }>) =>
    this.http.put<Community>(`${API_URL}/community`, body);

  deleteCommunity = (params: Nullable<{ password: string }>) =>
    this.http.post(`${API_URL}/community/delete`, params);

  getCommunityConditions = () =>
    this.http.get<ConditionsOfUse>(`${API_URL}/community/conditions`);

  updateCommunityConditions = (condition: ConditionsOfUseUpdate) =>
    this.http.put<ConditionsOfUse>(
      `${API_URL}/community/conditions`,
      condition
    );

  suspendCommunity = (suspend: boolean) =>
    this.http.put<Community>(
      `${API_URL}/community/suspend?suspend=${suspend}`,
      {}
    );

  getContractFiles = () =>
    this.http.get<ContractTemplate[]>(`${API_URL}/community/contract-files`);

  getCommunityAzureAd = () =>
    this.http.get<CommunityAzureAd>(`${API_URL}/community/azuread`);

  createAzureAdProvisioningToken = (months: number) =>
    this.http.post<CommunityAzureAd>(
      `${API_URL}/community/azuread-token`,
      `months=${months}`,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
      }
    );

  updateCommunityAzureAd = (body: CommunityAzureAd) =>
    this.http.put<CommunityAzureAd>(`${API_URL}/community/azuread`, body);

  getCommunityLdap = () =>
    this.http.get<LdapSettings>(`${API_URL}/community/ldap`);

  testCommunityLdap = (body: LdapSettings) =>
    this.http.put<LdapSettings>(`${API_URL}/community/ldaptest`, body);

  updateCommunityLdap = (body: LdapSettings) =>
    this.http.put<LdapSettings>(`${API_URL}/community/ldap`, body);

  sendTestNotification = () =>
    this.http.post<void>(`${API_URL}/community/sendtestnotification`, {});

  checkLoginName = (params: HttpParams) =>
    this.http.post<{ available: boolean }>(
      `${API_URL}/community/checkloginname`,
      null,
      {
        params,
      }
    );

  getCommunityConfig = () =>
    this.http.get<CommunityConfigResponse>(`${API_URL}/community/config`);

  updateNotificationSettings = (notificationSettings: NotificationSettings) =>
    this.http.put<NotificationSettings>(
      `${API_URL}/community/notificationsettings`,
      notificationSettings
    );

  uploadTemplate = (formData: FormData) =>
    this.http.post<CommunityTemplate>(
      `${API_URL}/community/templates`,
      formData,
      {
        reportUploadProgress: true,
        observe: 'events',
      }
    );

  wipeCommunityData = (params: Nullable<{ password: string }>) =>
    this.http.post<void>(`${API_URL}/community/wipe?async=true`, params);

  getNotificationSettings = () =>
    this.http.get<NotificationSettings>(
      `${API_URL}/community/notificationsettings`
    );

  getCommunityImageBase64 = () =>
    this.http.get<string>(`${API_URL}/community/image?base64=true`, {
      responseType: 'text' as 'json',
    });

  getCommunityImageWithUpdateDate = (updateDate: number) =>
    this.http.get<string | boolean>(
      `${API_URL}/community/image?base64=true&updateDate=${updateDate}`,
      {
        responseType: 'text' as 'json',
      }
    );

  getCommunityProcesses = () =>
    this.http.get<CommunityProcessStatus>(`${API_URL}/community/processes/4`);

  getCommunityTemplates = (type?: number) =>
    this.http.get<CommunityTemplate[]>(`${API_URL}/community/templates`, {
      params: type ? { type } : {},
    });

  editCommunity = async (resource: Community) => {
    const { EditCommunityModalComponent } =
      await import('../../modules/community/components/edit-community-modal.component');

    return this.modalService.open(
      EditCommunityModalComponent,
      { resource, rid: resource.rid },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  editCommunityNotifications = async (
    resource: Community,
    notificationSettings: NotificationSettings
  ) => {
    const { EditCommunityNotificationsModalComponent } =
      await import('../../modules/community/components/edit-community-notifications-modal.component');

    return this.modalService.open(
      EditCommunityNotificationsModalComponent,
      {
        resource: cloneDeep(resource),
        notificationSettings: cloneDeep(notificationSettings),
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  changeCommunityLogo = async (community: Community) => {
    const { EditCommunityLogoModalComponent } =
      await import('../../modules/community/components/edit-community-logo-modal.component');

    return this.modalService.open(
      EditCommunityLogoModalComponent,
      { community },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  wipeCommunity = async () => {
    const { WipeCommunityModalComponent } =
      await import('../../modules/community/components/wipe-community-modal.component');

    return this.modalService.open(WipeCommunityModalComponent);
  };

  editCommunityLDAP = async () => {
    const { EditCommunityLdapModalComponent } =
      await import('../../modules/community/components/edit-community-ldap-modal.component');

    return this.modalService.open(
      EditCommunityLdapModalComponent,
      {},
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  editCommunityAzureAd = async () => {
    const { EditAzureadModalComponent } =
      await import('../../modules/community/components/edit-azuread-modal.component');

    return this.modalService.open(
      EditAzureadModalComponent,
      {},
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  editCommunityConditions = async (community: Community) => {
    const { EditConditionsModalComponent } =
      await import('../../modules/community/components/edit-conditions-modal.component');

    return this.modalService.open(
      EditConditionsModalComponent,
      { community },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  deleteCommunityAzureAd = async () =>
    this.modalService.openConfirmation({
      title: 'WEB_COMMUNITY_AZUREAD_DELETE',
      text: 'WEB_DIALOGS_CONFIRM',
      successText: 'WEB_COMMUNITY_AZUREAD_DELETE_SUCCESS',
      request: { url: 'community/azuread', method: 'delete' },
      danger: true,
    });

  openTemplateNewForm = async (
    templates: { id?: number; name: string; type: number }[]
  ) => {
    const { NewTemplateModalComponent } =
      await import('../../modules/community/components/new-template-modal.component');

    return this.modalService.open(
      NewTemplateModalComponent,
      { templates },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  reindexCommunity = () =>
    this.modalService.openConfirmation({
      title: 'WEB_DIALOGS_COMMUNITY_REINDEX',
      text: 'WEB_DIALOGS_CONFIRM',
      request: { url: 'community/reindex?async=true', method: 'post' },
      successText: 'WEB_NOTIFY_COMMUNITY_REINDEX_SUCCESS',
    });

  activateOrganisationSignature = () =>
    this.modalService.openConfirmation({
      title: 'WEB_COMMUNITY_ACTIVATE_ORGANISATION_SIGNATURE',
      text: 'WEB_DIALOGS_CONFIRM',
      request: { url: 'community/signatureseal', method: 'put' },
      successText:
        'WEB_NOTIFY_COMMUNITY_ACTIVATE_ORGANISATION_SIGNATURE_SUCCESS',
    });

  deactivateOrganisationSignature = () =>
    this.modalService.openConfirmation({
      title: 'WEB_COMMUNITY_DEACTIVATE_ORGANISATION_SIGNATURE',
      text: 'WEB_DIALOGS_CONFIRM',
      successText:
        'WEB_NOTIFY_COMMUNITY_DEACTIVATE_ORGANISATION_SIGNATURE_SUCCESS',
      request: {
        url: 'community/signatureseal',
        method: 'delete',
      },
    });

  generateAndDownloadCEC = () =>
    this.modalService.generateCommunityCEC().then(() => {
      this.downloadService.notifyPreparing();

      this.http
        .post(`${getAbsoluteApiUrl()}/community/cec`, null, {
          responseType: 'arraybuffer',
        })
        .pipe(take(1))
        .subscribe((arrayBuffer) => {
          downloadBlob(
            new Blob([arrayBuffer], { type: 'application/pdf' }),
            'STR-CEC.pdf'
          );
        });
    });
}
